import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, MapPinIcon } from 'lucide-react';
// ─── Testimonial Data (Video) ────────────────────────────────────────────────
type VideoTestimonial = {
  vimeoId: string;
  initials: string;
  name: string;
  role: string;
  quote: string;
};
const videoTestimonials: VideoTestimonial[] = [{
  vimeoId: '1165844025',
  initials: 'W',
  name: 'Wanda Ramirez',
  role: 'Regional Leader | Camino Brillante',
  quote: 'Reclutas Digitales eliminó la improvisación en mi proceso de reclutamiento. Pasé de los métodos tradicionales a tener un sistema que me entrega prospectos calificados todos los días. Ahora mi equipo crece con predictibilidad y control.'
}, {
  vimeoId: '1165843949',
  initials: 'M',
  name: 'Marleny Fermin',
  role: 'Regional Leader | Camino Brillante',
  quote: 'Antes dependía de métodos tradicionales para reclutar. Con Reclutas Digitales tengo un sistema que trabaja para mí todos los días, generando prospectos calificados de forma constante. Mi negocio ahora tiene estructura real.'
}];
function VideoTestimonialCard({
  testimonial
}: {
  testimonial: VideoTestimonial;
}) {
  return <div className="flex flex-col gap-4 max-w-xs mx-auto w-full" data-id="element-999">
      {testimonial.vimeoId ? <div className="relative w-full rounded-2xl overflow-hidden shadow-lg shadow-black/40 border border-white/10" style={{
      paddingTop: '177.78%'
    }} data-id="element-1000">
          <iframe src={`https://player.vimeo.com/video/${testimonial.vimeoId}?badge=0&autopause=0&player_id=0&app_id=58479`} frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" className="absolute top-0 left-0 w-full h-full" title={`Testimonio ${testimonial.name}`} data-id="element-1001" />
        </div> : <div className="relative w-full rounded-2xl overflow-hidden shadow-lg shadow-black/40 border border-white/10 bg-bg/5" style={{
      paddingTop: '177.78%'
    }} data-id="element-1002">
          <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-1003">
            <div className="w-16 h-16 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center mb-3" data-id="element-1004">
              <svg className="w-7 h-7 text-accent/50" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-1005">
                <path d="M8 5v14l11-7z" data-id="element-1006" />
              </svg>
            </div>
            <p className="font-body text-bg/30 text-sm" data-id="element-1007">Video próximamente</p>
          </div>
        </div>}
      <div className="bg-bg/5 border border-white/10 rounded-2xl p-6" data-id="element-1008">
        <div className="flex items-center gap-0.5 mb-4 justify-center" data-id="element-1009">
          {[...Array(5)].map((_, i) => <svg key={i} className="w-4 h-4 text-accent" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true" data-id="element-1010">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-1011" />
            </svg>)}
        </div>
        <blockquote className="font-body text-bg/70 text-sm leading-relaxed text-center mb-5" data-id="element-1012">
          "{testimonial.quote}"
        </blockquote>
        <div className="text-center" data-id="element-1013">
          <p className="font-body font-semibold text-bg text-sm" data-id="element-1014">
            {testimonial.name}
          </p>
          <p className="font-body text-bg/45 text-xs mt-0.5" data-id="element-1015">
            {testimonial.role}
          </p>
        </div>
      </div>
    </div>;
}
function MetricCard({
  label,
  value,
  description
}: {
  label: string;
  value: string;
  description: string;
}) {
  return <div className="bg-bg/5 border border-white/8 rounded-xl p-6 hover:border-accent/25 transition-colors duration-200" data-id="element-1016">
      <p className="font-body text-bg/45 text-xs uppercase tracking-wider mb-2" data-id="element-1017">
        {label}
      </p>
      <p className="font-editorial font-bold text-3xl text-accent mb-2" data-id="element-1018">
        {value}
      </p>
      <p className="font-body text-bg/55 text-sm leading-relaxed" data-id="element-1019">
        {description}
      </p>
    </div>;
}
function FadeInSection({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 32
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-1020">
      {children}
    </motion.div>;
}
function Avatar({
  initials,
  color
}: {
  initials: string;
  color: string;
}) {
  return <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm text-white flex-shrink-0 ${color}`} data-id="element-1021">
      {initials}
    </div>;
}
export function ResultadosPage() {
  return <div className="bg-charcoal" data-id="element-1022">
      {/* HERO */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-id="element-1023">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1024">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-1025">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs font-semibold tracking-wide uppercase mb-6" data-id="element-1026">
              Resultados
            </span>
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-cream leading-tight tracking-tight mb-6" data-id="element-1027">
            Resultados verificables de{' '}
            <span className="text-accent" data-id="element-1028">líderes reales</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-lg sm:text-xl text-[#b8b4ae] mb-4 max-w-2xl mx-auto leading-relaxed" data-id="element-1029">
            No vendemos sueños. Mostramos el proceso que lo hizo posible.
          </motion.p>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.35
        }} className="text-xs text-[#7a7672] max-w-xl mx-auto" data-id="element-1030">
            Los resultados varían según mercado, producto y nivel de ejecución.
          </motion.p>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-border-subtle" data-id="element-1031">
        <div className="max-w-5xl mx-auto" data-id="element-1032">
          <FadeInSection data-id="element-1033">
            <h2 className="text-2xl sm:text-3xl font-bold text-cream text-center mb-12" data-id="element-1034">
              Lo que dicen quienes ya lo instalaron
            </h2>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-1035">
            <FadeInSection delay={0.1} data-id="element-1036">
              <div className="bg-surface border border-border-subtle rounded-2xl p-6 flex flex-col h-full" data-id="element-1037">
                <div className="flex-1 mb-5" data-id="element-1038">
                  <p className="text-[#b8b4ae] text-sm leading-relaxed italic" data-id="element-1039">
                    "Antes dependía 100% de referidos. Ahora tengo un flujo
                    constante de prospectos que yo controlo."
                  </p>
                </div>
                <div className="flex items-center gap-3 pt-4 border-t border-border-subtle" data-id="element-1040">
                  <Avatar initials="MG" color="bg-accent/70" data-id="element-1041" />
                  <div data-id="element-1042">
                    <span className="font-semibold text-cream text-sm block" data-id="element-1043">
                      María G.
                    </span>
                    <div className="flex items-center gap-1 text-xs text-[#7a7672]" data-id="element-1044">
                      <span data-id="element-1045">Agente de Seguros</span>
                      <span data-id="element-1046">·</span>
                      <MapPinIcon size={10} data-id="element-1047" />
                      <span data-id="element-1048">Texas</span>
                    </div>
                  </div>
                </div>
              </div>
            </FadeInSection>
            <FadeInSection delay={0.15} data-id="element-1049">
              <div className="bg-surface border border-border-subtle rounded-2xl p-6 flex flex-col h-full" data-id="element-1050">
                <div className="flex-1 mb-5" data-id="element-1051">
                  <p className="text-[#b8b4ae] text-sm leading-relaxed italic" data-id="element-1052">
                    "Instalamos el CRM en semana 1 y para semana 6 ya teníamos
                    el proceso documentado. El equipo opera solo."
                  </p>
                </div>
                <div className="flex items-center gap-3 pt-4 border-t border-border-subtle" data-id="element-1053">
                  <Avatar initials="CR" color="bg-[#2E3234] border border-accent/40" data-id="element-1054" />
                  <div data-id="element-1055">
                    <span className="font-semibold text-cream text-sm block" data-id="element-1056">
                      Carlos R.
                    </span>
                    <div className="flex items-center gap-1 text-xs text-[#7a7672]" data-id="element-1057">
                      <span data-id="element-1058">Dueño de Agencia</span>
                      <span data-id="element-1059">·</span>
                      <MapPinIcon size={10} data-id="element-1060" />
                      <span data-id="element-1061">Florida</span>
                    </div>
                  </div>
                </div>
              </div>
            </FadeInSection>
            <FadeInSection delay={0.2} data-id="element-1062">
              <div className="bg-surface border border-border-subtle rounded-2xl p-6 flex flex-col h-full" data-id="element-1063">
                <div className="flex-1 mb-5" data-id="element-1064">
                  <p className="text-[#b8b4ae] text-sm leading-relaxed italic" data-id="element-1065">
                    "Lo que más valoro es la estructura. Por primera vez sé
                    exactamente qué está funcionando y qué necesito ajustar."
                  </p>
                </div>
                <div className="flex items-center gap-3 pt-4 border-t border-border-subtle" data-id="element-1066">
                  <Avatar initials="AL" color="bg-elevated border border-border-subtle" data-id="element-1067" />
                  <div data-id="element-1068">
                    <span className="font-semibold text-cream text-sm block" data-id="element-1069">
                      Ana L.
                    </span>
                    <div className="flex items-center gap-1 text-xs text-[#7a7672]" data-id="element-1070">
                      <span data-id="element-1071">Agente Senior</span>
                      <span data-id="element-1072">·</span>
                      <MapPinIcon size={10} data-id="element-1073" />
                      <span data-id="element-1074">California</span>
                    </div>
                  </div>
                </div>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* MÉTRICAS Y VIDEO TESTIMONIOS */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-charcoal" data-id="element-1075">
        <div className="max-w-6xl mx-auto" data-id="element-1076">
          <FadeInSection data-id="element-1077">
            <div className="text-center mb-14" data-id="element-1078">
              <div className="inline-block border border-accent/30 text-accent text-xs tracking-widest uppercase rounded-full px-4 py-1.5 mb-5 font-body" data-id="element-1079">
                Resultados
              </div>
              <h2 className="font-editorial font-bold text-3xl sm:text-4xl md:text-5xl text-bg mb-4" data-id="element-1080">
                Resultados medibles, sin humo.
              </h2>
              <p className="font-body text-bg/55 text-lg max-w-2xl mx-auto" data-id="element-1081">
                Métricas reales de los sistemas que instalamos. Sin promesas de
                ingresos.
              </p>
            </div>
          </FadeInSection>

          {/* Metric cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16" data-id="element-1082">
            <FadeInSection delay={0.05} data-id="element-1083">
              <MetricCard label="Velocidad de respuesta" value="< 5 min" description="Tiempo promedio de primer contacto con leads" data-id="element-1084" />
            </FadeInSection>
            <FadeInSection delay={0.1} data-id="element-1085">
              <MetricCard label="Contactabilidad" value="+40%" description="Mejora en tasa de contacto con prospectos" data-id="element-1086" />
            </FadeInSection>
            <FadeInSection delay={0.15} data-id="element-1087">
              <MetricCard label="Show rate" value="+60%" description="Asistencia a citas programadas con sistema" data-id="element-1088" />
            </FadeInSection>
            <FadeInSection delay={0.2} data-id="element-1089">
              <MetricCard label="Consistencia de citas" value="Semanal" description="Flujo constante de citas sin depender de referidos" data-id="element-1090" />
            </FadeInSection>
            <FadeInSection delay={0.25} data-id="element-1091">
              <MetricCard label="Seguimiento" value="100%" description="Prospectos con seguimiento estructurado y automatizado" data-id="element-1092" />
            </FadeInSection>
            <FadeInSection delay={0.3} data-id="element-1093">
              <MetricCard label="Reclutamiento con sistema" value="Activo" description="Pipeline de reclutamiento funcionando en paralelo" data-id="element-1094" />
            </FadeInSection>
          </div>

          {/* Video Testimonials */}
          <FadeInSection delay={0.15} data-id="element-1095">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-1096">
              {videoTestimonials.map((t, i) => <VideoTestimonialCard key={i} testimonial={t} data-id="element-1097" />)}
            </div>
          </FadeInSection>

          <FadeInSection delay={0.2} data-id="element-1098">
            <div className="mt-8 text-center" data-id="element-1099">
              <p className="font-body text-bg/35 text-xs" data-id="element-1100">
                * Testimonios individuales. Los resultados varían según el
                esfuerzo y contexto de cada persona.
              </p>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* ANTES / DESPUÉS */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface" data-id="element-1101">
        <div className="max-w-5xl mx-auto" data-id="element-1102">
          <FadeInSection data-id="element-1103">
            <div className="text-center mb-12" data-id="element-1104">
              <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-3" data-id="element-1105">
                Antes y después del sistema
              </h2>
              <p className="text-[#b8b4ae]" data-id="element-1106">
                El cambio real está en el proceso, no en los números.
              </p>
            </div>
          </FadeInSection>

          <div className="space-y-5" data-id="element-1107">
            <FadeInSection delay={0.1} data-id="element-1108">
              <div className="bg-elevated border border-border-subtle rounded-2xl overflow-hidden" data-id="element-1109">
                <div className="grid grid-cols-1 sm:grid-cols-2" data-id="element-1110">
                  <div className="p-6 sm:p-8 border-b sm:border-b-0 sm:border-r border-border-subtle" data-id="element-1111">
                    <span className="text-xs font-semibold text-red-400 uppercase tracking-widest block mb-4" data-id="element-1112">
                      Antes
                    </span>
                    <ul className="space-y-2.5" data-id="element-1113">
                      <li className="flex items-start gap-2 text-sm text-[#7a7672]" data-id="element-1114">
                        <span className="text-red-500 mt-0.5" data-id="element-1115">×</span>
                        Sin proceso documentado
                      </li>
                      <li className="flex items-start gap-2 text-sm text-[#7a7672]" data-id="element-1116">
                        <span className="text-red-500 mt-0.5" data-id="element-1117">×</span>
                        Dependencia total de referidos
                      </li>
                      <li className="flex items-start gap-2 text-sm text-[#7a7672]" data-id="element-1118">
                        <span className="text-red-500 mt-0.5" data-id="element-1119">×</span>
                        Sin métricas ni visibilidad
                      </li>
                    </ul>
                  </div>
                  <div className="p-6 sm:p-8" data-id="element-1120">
                    <span className="text-xs font-semibold text-accent uppercase tracking-widest block mb-4" data-id="element-1121">
                      Después
                    </span>
                    <ul className="space-y-2.5" data-id="element-1122">
                      <li className="flex items-start gap-2 text-sm text-cream" data-id="element-1123">
                        <span className="text-accent mt-0.5" data-id="element-1124">✓</span>
                        Sistema instalado y documentado
                      </li>
                      <li className="flex items-start gap-2 text-sm text-cream" data-id="element-1125">
                        <span className="text-accent mt-0.5" data-id="element-1126">✓</span>
                        Leads propios activos y controlados
                      </li>
                      <li className="flex items-start gap-2 text-sm text-cream" data-id="element-1127">
                        <span className="text-accent mt-0.5" data-id="element-1128">✓</span>
                        Dashboard operativo con datos reales
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="px-6 sm:px-8 py-3 border-t border-border-subtle bg-surface/50" data-id="element-1129">
                  <span className="text-xs text-[#7a7672]" data-id="element-1130">
                    Agente individual — Mercado hispano, EE.UU.
                  </span>
                </div>
              </div>
            </FadeInSection>

            <FadeInSection delay={0.15} data-id="element-1131">
              <div className="bg-elevated border border-border-subtle rounded-2xl overflow-hidden" data-id="element-1132">
                <div className="grid grid-cols-1 sm:grid-cols-2" data-id="element-1133">
                  <div className="p-6 sm:p-8 border-b sm:border-b-0 sm:border-r border-border-subtle" data-id="element-1134">
                    <span className="text-xs font-semibold text-red-400 uppercase tracking-widest block mb-4" data-id="element-1135">
                      Antes
                    </span>
                    <ul className="space-y-2.5" data-id="element-1136">
                      <li className="flex items-start gap-2 text-sm text-[#7a7672]" data-id="element-1137">
                        <span className="text-red-500 mt-0.5" data-id="element-1138">×</span>
                        Agentes sin proceso claro
                      </li>
                      <li className="flex items-start gap-2 text-sm text-[#7a7672]" data-id="element-1139">
                        <span className="text-red-500 mt-0.5" data-id="element-1140">×</span>
                        Dueño como cuello de botella
                      </li>
                      <li className="flex items-start gap-2 text-sm text-[#7a7672]" data-id="element-1141">
                        <span className="text-red-500 mt-0.5" data-id="element-1142">×</span>
                        CRM sin usar o inexistente
                      </li>
                    </ul>
                  </div>
                  <div className="p-6 sm:p-8" data-id="element-1143">
                    <span className="text-xs font-semibold text-accent uppercase tracking-widest block mb-4" data-id="element-1144">
                      Después
                    </span>
                    <ul className="space-y-2.5" data-id="element-1145">
                      <li className="flex items-start gap-2 text-sm text-cream" data-id="element-1146">
                        <span className="text-accent mt-0.5" data-id="element-1147">✓</span>
                        Proceso de ventas documentado por agente
                      </li>
                      <li className="flex items-start gap-2 text-sm text-cream" data-id="element-1148">
                        <span className="text-accent mt-0.5" data-id="element-1149">✓</span>
                        Equipo opera con autonomía estructurada
                      </li>
                      <li className="flex items-start gap-2 text-sm text-cream" data-id="element-1150">
                        <span className="text-accent mt-0.5" data-id="element-1151">✓</span>
                        GHL configurado con pipelines activos
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="px-6 sm:px-8 py-3 border-t border-border-subtle bg-surface/50" data-id="element-1152">
                  <span className="text-xs text-[#7a7672]" data-id="element-1153">
                    Agencia en crecimiento — Multi-estado, EE.UU.
                  </span>
                </div>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="py-12 px-4 sm:px-6 lg:px-8" data-id="element-1154">
        <div className="max-w-3xl mx-auto" data-id="element-1155">
          <FadeInSection data-id="element-1156">
            <div className="bg-surface border border-border-subtle rounded-xl p-6" data-id="element-1157">
              <p className="text-xs text-[#7a7672] leading-relaxed" data-id="element-1158">
                <strong className="text-[#b8b4ae]" data-id="element-1159">Aviso legal:</strong> Los
                testimonios y casos presentados reflejan experiencias
                individuales. Los resultados varían significativamente según el
                mercado, producto, nivel de ejecución y otros factores. Reclutas
                Digitales no garantiza ingresos, leads ni resultados
                específicos. La información presentada tiene fines ilustrativos
                únicamente.
              </p>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-surface" data-id="element-1160">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1161">
          <FadeInSection data-id="element-1162">
            <div className="bg-elevated border border-accent/20 rounded-3xl p-10 sm:p-14" data-id="element-1163">
              <span className="inline-block w-12 h-1 bg-accent rounded-full mb-6" data-id="element-1164" />
              <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-4" data-id="element-1165">
                ¿Quieres construir tu propio caso de éxito?
              </h2>
              <p className="text-[#b8b4ae] mb-8 leading-relaxed" data-id="element-1166">
                El sistema está disponible. El proceso está documentado. El
                siguiente paso es tuyo.
              </p>
              <a href="/contact-us" className="inline-flex items-center gap-2 px-8 py-4 bg-accent hover:bg-accent-hover text-charcoal font-bold rounded-xl transition-colors duration-200 text-base" data-id="element-1167">
                Agendar diagnóstico
                <ArrowRightIcon size={18} data-id="element-1168" />
              </a>
            </div>
          </FadeInSection>
        </div>
      </section>
    </div>;
}